import { Head, Link, useMutation, useRouter } from 'arcway/lib/client'

import { AuthPageShell } from '#components/auth-page-shell.jsx'
import { Button } from '#components/ui/button.jsx'
import { ErrorAlert } from '#components/ui/error-alert.jsx'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '#components/ui/field.jsx'
import { Input } from '#components/ui/input.jsx'
import { PasswordInput } from '#components/ui/password-input.jsx'

import { useGuestSession } from '../hooks/use-guest-session.js'
import {
  authEmailPrefill,
  authSwitchHref,
  completeAuth,
} from '../lib/client/auth.js'
import { safeReturnPath } from '../lib/client/safe-return-path.js'

export default function SignupPage() {
  const router = useRouter()
  const next = safeReturnPath(router.query.next)
  const email = authEmailPrefill(router.query.email)
  const session = useGuestSession(next)
  const request = useMutation('/auth/signup', { method: 'POST' })

  async function handleSubmit(event) {
    event.preventDefault()
    if (request.loading) return

    const data = new FormData(event.currentTarget)
    try {
      await completeAuth(request, session, router, next, {
        email: data.get('email'),
        name: data.get('name') || undefined,
        password: data.get('password'),
      })
    } catch {}
  }

  return (
    <>
      <Head
        description='Create your Mono account and start publishing your podcast.'
        title='Create account — mono.fm'
      />
      <AuthPageShell
        kind='signup'
        description='Start publishing your podcast from one focused workspace.'
        loading={session.loading || session.authenticated}
        title='Create your Mono account'
      >
        <form className='space-y-6' onSubmit={handleSubmit}>
          {request.error && <ErrorAlert>{request.error.message}</ErrorAlert>}
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor='name'>Name</FieldLabel>
              <Input
                aria-describedby='name-description'
                autoComplete='name'
                disabled={request.loading}
                id='name'
                maxLength='160'
                name='name'
                placeholder='Your name'
              />
              <FieldDescription id='name-description'>
                Optional. You can change this later.
              </FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor='email'>Email</FieldLabel>
              <Input
                autoComplete='email'
                defaultValue={email}
                disabled={request.loading}
                id='email'
                maxLength='320'
                name='email'
                placeholder='you@example.com'
                required
                type='email'
              />
            </Field>
            <Field>
              <FieldLabel htmlFor='password'>Password</FieldLabel>
              <PasswordInput
                aria-describedby='password-description'
                autoComplete='new-password'
                disabled={request.loading}
                id='password'
                maxLength='1024'
                minLength='6'
                name='password'
                required
                type='password'
              />
              <FieldDescription id='password-description'>
                Use at least 6 characters.
              </FieldDescription>
            </Field>
          </FieldGroup>
          <Button
            className='w-full'
            loading={request.loading}
            size='lg'
            type='submit'
          >
            Create account
          </Button>
          {request.loading && (
            <p className='auth-pending' role='status'>
              Creating your account…
            </p>
          )}
        </form>
        <p className='mt-6 text-center text-sm text-muted-foreground'>
          Already have an account?{' '}
          <Link
            className='font-medium text-foreground underline-offset-4 hover:underline'
            href={authSwitchHref('/login', router.query.next)}
          >
            Log in
          </Link>
        </p>
      </AuthPageShell>
    </>
  )
}
